import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { StationItem } from '@/types';

import StatusMessage from '@/components/shared/status-message';
import StationListStickyContent from '@/components/stations/station-list-sticky-content';
import StationSummary from '@/components/stations/station-summary';
import VirtualizedList from '@/components/ui/virtualized-list';
import { useGetIncidentListData } from '@/hooks/use-get-incident-list-data';
import useGetStationListData from '@/hooks/use-get-station-list-data';
import useStationSearch from '@/hooks/use-station-search';
import { ERROR_FEEDBACK, LIST, TODAY } from '@/utils/constants';
import { SEARCH_PARAM_SHOW } from '@/utils/constants/queries';
import isIncidentActiveOnDate from '@/utils/helpers/is-incident-active-on-date';

const StationList = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { activeSearchKey } = useStationSearch(LIST);
  const currentShowParam = searchParams.get(SEARCH_PARAM_SHOW);

  const {
    isLoading: isStationListLoading,
    isError: isStationListError,
    data: stationList,
    refetch: refetchStationList,
  } = useGetStationListData();

  const { data: incidents = [] } = useGetIncidentListData();

  const affectedStationIds = useMemo(() => {
    const activeIds = new Set<string>();

    incidents.forEach((inc) => {
      const isActive = inc.activePeriod?.some(({ start, end }) =>
        isIncidentActiveOnDate(start, end, TODAY)
      );

      if (isActive && inc.affectedStops) {
        inc.affectedStops.forEach((stop) => activeIds.add(stop.id));
      }
    });

    return activeIds;
  }, [incidents, TODAY]);

  const filteredStations = useMemo(() => {
    if (!activeSearchKey) {
      return stationList || [];
    }

    const searchLower = activeSearchKey.toLocaleLowerCase();

    return (
      stationList?.filter((item: StationItem) =>
        item.designationOfficial.toLocaleLowerCase().includes(searchLower)
      ) || []
    );
  }, [stationList, activeSearchKey]);

  const renderStationItem = useCallback(
    (station: StationItem) => {
      const { id, designationOfficial, stopType } = station ?? {};

      return (
        <StationSummary
          key={id}
          name={designationOfficial}
          stationId={id}
          isDisabled={stopType === 'operational'}
          hasIncident={affectedStationIds.has(id)}
        />
      );
    },
    [affectedStationIds]
  );

  const errorView = (
    <div className="min-h-[60vh] flex items-center justify-center py-8">
      <StatusMessage
        type="error"
        title={t(ERROR_FEEDBACK.general)}
        description={t(ERROR_FEEDBACK.stations.api)}
        showReloadButton
        onRetry={refetchStationList}
      />
    </div>
  );

  const emptyView = (
    <div className="min-h-[60vh] flex items-center justify-center py-8">
      <StatusMessage
        type="empty"
        title={t(ERROR_FEEDBACK.stations.empty)}
        description={t(ERROR_FEEDBACK.stations.description)}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pb-4">
        <StationListStickyContent />
      </div>
      <VirtualizedList
        data={filteredStations}
        isLoading={isStationListLoading}
        isError={isStationListError}
        resetDependencies={[activeSearchKey]}
        memoryKey={currentShowParam}
        emptyView={emptyView}
        errorView={errorView}
        renderItem={renderStationItem}
      />
    </div>
  );
};

export default StationList;
